import React, { useEffect } from 'react';

const BASE_URL = 'https://www.deallabce.com';

export default function SEO({ 
  title, 
  description, 
  keywords,
  image,
  url,
  type = 'website',
  structuredData,
  noindex = false
}) {
  // Build title string
  let fullTitle = 'DealLab CE | Texas Real Estate Continuing Education | TREC Approved';
  if (title && typeof title === 'string') {
    fullTitle = title + ' | DealLab CE';
  }
    
  const metaDescription = description || 
    'Professional TREC-approved continuing education courses for Texas real estate agents. Learn DSCR loans, investment strategies, and more from industry experts.';
    
  const metaImage = image || `${BASE_URL}/images/deallab-og-image.png`;
  const canonicalUrl = url ? `${BASE_URL}${url}` : BASE_URL;
  
  const defaultKeywords = 'Texas real estate continuing education, TREC approved courses, CE credits, real estate agent training';
  const metaKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to update or create meta tags
    const updateMeta = (name, content, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Update meta tags
    updateMeta('title', fullTitle);
    updateMeta('description', metaDescription);
    updateMeta('keywords', metaKeywords);
    updateMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph
    updateMeta('og:type', type, true);
    updateMeta('og:url', canonicalUrl, true);
    updateMeta('og:title', fullTitle, true);
    updateMeta('og:description', metaDescription, true);
    updateMeta('og:image', metaImage, true);
    updateMeta('og:site_name', 'DealLab CE', true);

    // Twitter
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:url', canonicalUrl);
    updateMeta('twitter:title', fullTitle);
    updateMeta('twitter:description', metaDescription);
    updateMeta('twitter:image', metaImage);

    // Structured Data
    if (structuredData) {
      let script = document.querySelector('script[data-seo="structured-data"]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo', 'structured-data');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }
  }, [fullTitle, metaDescription, metaKeywords, canonicalUrl, metaImage, type, noindex, structuredData]);

  return null; // This component doesn't render anything visible
}

// Pre-defined SEO configurations for common pages
export const PageSEO = {
  home: {
    title: 'Texas Real Estate Continuing Education',
    description: 'Professional TREC-approved continuing education courses for Texas real estate agents. Learn DSCR loans, investment strategies, and more from industry experts. Book private courses for your team.',
    keywords: 'Texas real estate CE, TREC approved, continuing education, real estate license renewal',
    url: '/public'
  },
  courses: {
    title: 'CE Courses & Training',
    description: 'Browse our catalog of TREC-approved continuing education courses including DSCR loans, investment property strategies, and professional development training for real estate agents.',
    keywords: 'real estate courses, CE courses, DSCR loan training, investment property education',
    url: '/public/courses'
  },
  schedule: {
    title: 'Course Schedule',
    description: 'View upcoming continuing education course dates and times. Register for in-person classroom or webinar CE courses for Texas real estate agents.',
    keywords: 'CE course schedule, real estate class schedule, upcoming courses, register for CE',
    url: '/public/schedule'
  },
  instructors: {
    title: 'Our Instructors',
    description: 'Meet our certified instructors who are industry experts in real estate finance, investment strategies, and Texas real estate regulations.',
    keywords: 'CE instructors, real estate teachers, certified instructors, TREC approved instructors',
    url: '/public/instructors'
  },
  sponsors: {
    title: 'Become a Sponsor',
    description: 'Partner with DealLab CE to reach Texas real estate professionals. Sponsor our continuing education courses and showcase your brand to engaged agents and brokers.',
    keywords: 'CE sponsorship, real estate advertising, sponsor CE courses, reach real estate agents',
    url: '/public/sponsors'
  },
  request: {
    title: 'Book a Private Course',
    description: 'Request a private continuing education course for your team or brokerage. Customize CE training sessions for your real estate professionals.',
    keywords: 'private CE course, team training, brokerage CE, custom real estate training',
    url: '/public/request'
  }
};

// Structured data generators
export const generateCourseStructuredData = (course) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  "name": course.title,
  "description": course.description,
  "provider": {
    "@type": "Organization",
    "name": "DealLab CE",
    "sameAs": "https://www.deallabce.com"
  },
  "educationalCredentialAwarded": course.course_type === 'ce' ? "Continuing Education Credit" : "Professional Development Certificate",
  "timeRequired": `PT${course.credit_hours}H`,
  "hasCourseInstance": {
    "@type": "CourseInstance",
    "courseMode": ["onsite", "online"]
  }
});

export const generateInstructorStructuredData = (instructor) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": instructor.name,
  "jobTitle": instructor.title || "CE Instructor",
  "description": instructor.bio,
  "worksFor": {
    "@type": "Organization",
    "name": "DealLab CE"
  }
});

export const generateEventStructuredData = (event, course) => ({
  "@context": "https://schema.org",
  "@type": "EducationEvent",
  "name": course?.title || event.course_title,
  "startDate": event.scheduled_date,
  "location": event.delivery_format === 'webinar' ? {
    "@type": "VirtualLocation",
    "url": "https://www.deallabce.com"
  } : {
    "@type": "Place",
    "name": event.venue_name || event.location,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": event.city,
      "addressRegion": event.state || "TX",
      "addressCountry": "US"
    }
  },
  "organizer": {
    "@type": "Organization",
    "name": "DealLab CE",
    "url": "https://www.deallabce.com"
  },
  "eventAttendanceMode": event.delivery_format === 'webinar' 
    ? "https://schema.org/OnlineEventAttendanceMode"
    : "https://schema.org/OfflineEventAttendanceMode"
});
